import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ActiveOrdersSummary {
  count: number;
  totalBudget: number;
}

export interface OrderListRow {
  id: string;
  reference: string;
  team_member_name: string;
  celebration_type: string;
  delivery_date: string | null;
  status: string;
  payment_status: string | null;
  budget_cents: number;
  created_at: string | null;
  auto_approved: boolean | null;
  // Sprint 1.7r: surface chat / browse-originated orders ("gift requests")
  // distinctly on /orders. The marker is `·` inside cake_size, see
  // src/lib/giftRequest.ts for the parser. We over-fetch two extra columns
  // so the row card can render the vendor + product line without a join.
  cake_type: string | null;
  cake_size: string | null;
  // MS214: vendor_category drives category-aware status-badge labels
  // (e.g. "Baking" for cakes vs "Packing" for hampers — the database
  // status enum stays "baking" universally but the label needs to
  // read right for the actual product).
  vendor_category: string | null;
  // Sprint 2.5a B.2-fix-1 (P0-2), image_url resolved post-fetch via
  // celebration_recommendations.line_items[0].sku_id → vendor_products.
  // Null for legacy cake orders (no recommendation linkage) and for
  // chat orders where the SKU has no image yet (catalogue gap).
  // Surfaces in GiftRequestCard with category-tile fallback when null.
  image_url: string | null;
  // MS64d, resolved alongside image_url. Currently unused by the list
  // row card (image is enough) but exposed for parity with detail.
  sku_id: string | null;
}

// "Action needed" is a derived UI status: the DB knows status="pending"
// + payment_status!="paid", but the user needs that surfaced as
// something requiring their attention (complete the form, finish
// payment), not as "waiting on the bakery". Centralise the rule here
// so the list, detail, badge, and filter all agree.
export function isOrderActionNeeded(order: Pick<OrderListRow, "status" | "payment_status">): boolean {
  if (order.status === "delivered" || order.status === "cancelled") return false;
  return order.status === "pending" && order.payment_status !== "paid";
}

export interface OrderDetailRow {
  id: string;
  reference: string;
  team_member_name: string;
  team_member_id: string | null;
  celebration_type: string;
  cake_type: string | null;
  cake_size: string | null;
  // MS214: parity with OrderListRow — category drives the
  // status-badge label ("Baking" vs "Packing" vs "Preparing").
  vendor_category: string | null;
  dietary_needs: string[] | null;
  special_instructions: string | null;
  delivery_address: string | null;
  delivery_date: string | null;
  delivery_time: string | null;
  budget_cents: number;
  status: string;
  payment_status: string | null;
  stripe_invoice_pdf: string | null;
  notes: string | null;
  company_id: string | null;
  celebration_id: string | null;
  auto_approved: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  // Sprint 2.3a, public gift-share token; used by ShareGiftButton on
  // /orders/:id to surface the copy-link CTA. Backfilled by
  // 20260514020000_orders_gift_share_token.sql.
  gift_share_token: string | null;
  // Sprint 2.5a B.2-fix-1 (P0-2), image_url resolved post-fetch via
  // celebration_recommendations.line_items[0].sku_id → vendor_products.
  // Null for legacy cake orders or when the SKU has no image_url set.
  image_url: string | null;
  // MS64d, sku_id resolved post-fetch from celebration_recommendations
  // .line_items[0].sku_id. Lets /orders/:id call useSkuDetail() to
  // hydrate the rich vendor_products row (long_description, voucher_
  // terms, contents_list, included_inclusions, lead_time_days) without
  // a second join on the list query. Null for legacy cake orders and
  // for chat orders whose recommendation rec is missing or malformed.
  sku_id: string | null;
}

export interface BillingOrderRow {
  id: string;
  reference: string;
  team_member_name: string;
  celebration_type: string;
  budget_cents: number;
  payment_status: string | null;
  status: string;
  delivery_date: string | null;
  stripe_invoice_pdf: string | null;
  created_at: string;
  auto_approved: boolean | null;
}

/**
 * Single order by id. Used by /orders/:id (customer) and admin detail
 * views. Callers mutating the row should setQueryData on this key for
 * optimistic updates, then invalidate.
 */
export function useOrderById(
  id: string | null | undefined,
  options?: {
    /**
     * Re-fetch interval in ms while a predicate returns true. Used by
     * /orders/:id/celebrate to poll the order until the Stripe webhook
     * has flipped payment_status to "paid", so the celebrate UI doesn't
     * paint over a still-processing order. Disable polling once the
     * predicate goes false (the predicate gets the freshly-fetched row
     * each tick).
     */
    pollWhile?: (row: OrderDetailRow | null) => boolean;
    pollIntervalMs?: number;
  },
) {
  return useQuery({
    queryKey: ["orders", "detail", id],
    enabled: Boolean(id),
    queryFn: async (): Promise<OrderDetailRow | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;
      const order = data as unknown as Omit<OrderDetailRow, "image_url" | "sku_id"> | null;
      if (!order) return null;
      // Sprint 2.5a B.2-fix-1 (P0-2), same image enrichment pattern as
      // useOrdersList, scoped to a single order. Same null-safety
      // chain: no recommendation → no line_items → no sku_id → no
      // SKU → no image_url. The detail card's fallback (category
      // tile or text-only) handles the null case cleanly.
      const enriched = await enrichOrdersWithImageUrl([order]);
      return enriched[0] ?? null;
    },
    refetchInterval: options?.pollWhile
      ? (query) => {
          const data = query.state.data as OrderDetailRow | null | undefined;
          return options.pollWhile!(data ?? null) ? options.pollIntervalMs ?? 2_000 : false;
        }
      : undefined,
  });
}

/**
 * Billing-view orders: newest first, capped at 50 rows, with payment status
 * and the Stripe invoice PDF link. Used by /billing. The shape is different
 * from useOrdersList on purpose, billing cares about payment_status and
 * stripe_invoice_pdf, the orders page does not.
 */
export function useBillingHistory(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["orders", companyId, "billing-history"],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<BillingOrderRow[]> => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("orders")
        .select(
          "id, reference, team_member_name, celebration_type, budget_cents, payment_status, status, delivery_date, stripe_invoice_pdf, created_at, auto_approved",
        )
        .eq("company_id", companyId)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data as BillingOrderRow[] | null) ?? [];
    },
  });
}

/**
 * Full order list for a company, newest first. Used by the customer-side
 * /orders page. Tab filtering is a pure client-side derivation so every
 * tab hits the same cached query.
 */
export function useOrdersList(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["orders", companyId, "list"],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<OrderListRow[]> => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("orders")
        .select("id, reference, team_member_name, celebration_type, delivery_date, status, payment_status, budget_cents, created_at, auto_approved, cake_type, cake_size, vendor_category")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false })
        // MS397: cap unbounded list; 100 covers any realistic company's
        // recent order history and prevents full-table scans as the platform scales.
        .limit(100);
      if (error) throw error;
      const orders = (data as Omit<OrderListRow, "image_url" | "sku_id">[] | null) ?? [];
      // Sprint 2.5a B.2-fix-1 (P0-2), batch-resolve image_url via
      // celebration_recommendations.line_items[0].sku_id. Two extra
      // queries (recommendations IN orderIds, then vendor_products
      // IN skuIds) regardless of order count, far cheaper than a per-
      // row fetch loop. Failures are silent (image_url stays null and
      // the card uses its fallback) so the orders list never blocks
      // on this enrichment.
      return await enrichOrdersWithImageUrl(orders);
    },
  });
}

/**
 * Sprint 2.5a B.2-fix-1, shared helper. Given an array of orders,
 * batch-resolves vendor_products.image_url via celebration_recommendations
 * and returns a new array with image_url merged in. Reused by
 * useOrdersList + useRecentGiftRequests so both surfaces show real
 * product photography on chat-originated rows.
 *
 * Null-safe at every step: orders without a recommendation, recs
 * without line_items, line_items[0] without sku_id, and SKUs without
 * image_url all just leave image_url=null on the order row.
 */
async function enrichOrdersWithImageUrl<T extends { id: string }>(
  orders: T[],
): Promise<(T & { image_url: string | null; sku_id: string | null })[]> {
  if (orders.length === 0) return [];
  const orderIds = orders.map((o) => o.id);
  const { data: recsData } = await supabase
    .from("celebration_recommendations")
    .select("order_id, line_items, created_at")
    .in("order_id", orderIds)
    .order("created_at", { ascending: false });
  type RecRow = { order_id: string | null; line_items: unknown };
  const recs = (recsData as RecRow[] | null) ?? [];
  // For orders with multiple recs (rare, adjustment chains), the most
  // recent wins because we pre-sorted DESC and Map.set overwrites.
  // Walking in REVERSE so the first (newest) write sticks.
  const skuByOrder = new Map<string, string>();
  for (const r of [...recs].reverse()) {
    if (!r.order_id) continue;
    const lineItems = r.line_items;
    if (!Array.isArray(lineItems) || lineItems.length === 0) continue;
    const first = lineItems[0] as { sku_id?: unknown } | null;
    if (first && typeof first.sku_id === "string") {
      skuByOrder.set(r.order_id, first.sku_id);
    }
  }
  const skuIds = Array.from(new Set(skuByOrder.values()));
  const imageBySkuId = new Map<string, string>();
  if (skuIds.length > 0) {
    const { data: skusData } = await supabase
      .from("vendor_products")
      .select("id, image_url")
      .in("id", skuIds);
    type SkuRow = { id: string; image_url: string | null };
    for (const s of (skusData as SkuRow[] | null) ?? []) {
      if (s.image_url) imageBySkuId.set(s.id, s.image_url);
    }
  }
  return orders.map((o) => {
    const skuId = skuByOrder.get(o.id) ?? null;
    return {
      ...o,
      image_url: skuId ? (imageBySkuId.get(skuId) ?? null) : null,
      // MS64d, expose the resolved sku_id so OrderDetail can call
      // useSkuDetail() and hydrate the rich vendor_products fields.
      // Null when the order has no recommendation row or no line_items.
      sku_id: skuId,
    };
  });
}

/**
 * Recent chat / browse-originated orders ("gift requests") for the
 * dashboard tile. Filters server-side on `cake_size LIKE '%·%'` so we
 * don't pull legacy cake orders into the customer's "you recently
 * ordered" surface, that lives on /orders. Used by Dashboard to
 * surface a freshly-reconciled customer's order so they don't land on
 * the empty calendar wondering where their gift went.
 */
export function useRecentGiftRequests(
  companyId: string | null | undefined,
  limit = 3,
) {
  return useQuery({
    queryKey: ["orders", companyId, "recent-gift-requests", limit],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<OrderListRow[]> => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("orders")
        .select("id, reference, team_member_name, celebration_type, delivery_date, status, payment_status, budget_cents, created_at, auto_approved, cake_type, cake_size, vendor_category")
        .eq("company_id", companyId)
        .like("cake_size", "%·%")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      const orders = (data as Omit<OrderListRow, "image_url" | "sku_id">[] | null) ?? [];
      // Sprint 2.5a B.2-fix-1 (P0-2), same image enrichment as the
      // full /orders list. Only chat/browse rows reach this hook (the
      // `cake_size LIKE '%·%'` filter), so EVERY row should resolve to
      // an image_url unless the SKU has none in vendor_products.
      return await enrichOrdersWithImageUrl(orders);
    },
  });
}

const ACTIVE_STATUSES_FOR_COUNT = ["pending", "confirmed"];
// MS341: "ready" replaces "out_for_delivery" — DB enum drift fix.
// Pre-MS341 orders that the vendor marked Ready slipped out of the
// dashboard's active-spend total because the filter looked for the
// never-written "out_for_delivery" string.
const ACTIVE_STATUSES_FOR_SPEND = ["pending", "confirmed", "baking", "ready"];

/**
 * Count + summed budget for in-flight orders. Dashboard shows both next to
 * each other, and they share the same company scope, so fetching together
 * keeps keys aligned and invalidations simple.
 */
export function useActiveOrdersSummary(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["orders", companyId, "active-summary"],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<ActiveOrdersSummary> => {
      if (!companyId) return { count: 0, totalBudget: 0 };

      const [countRes, spendRes] = await Promise.all([
        supabase
          .from("orders")
          .select("id", { count: "exact", head: true })
          .eq("company_id", companyId)
          .in("status", ACTIVE_STATUSES_FOR_COUNT),
        supabase
          .from("orders")
          .select("budget_cents")
          .eq("company_id", companyId)
          .in("status", ACTIVE_STATUSES_FOR_SPEND),
      ]);

      if (countRes.error) throw countRes.error;
      if (spendRes.error) throw spendRes.error;

      const rows = (spendRes.data as { budget_cents: number | null }[] | null) ?? [];
      // MS551-followup-A: sum in cents, divide once at the API boundary
      // so totalBudget keeps its dollar semantics for existing callers.
      const totalCents = rows.reduce(
        (sum, row) => sum + (row.budget_cents ?? 0),
        0,
      );
      const totalBudget = totalCents / 100;

      return { count: countRes.count ?? 0, totalBudget };
    },
  });
}
